import { useState, useEffect, useMemo, useRef } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useParams, useNavigate } from "react-router-dom";
import { TrendDropdown } from "./TrendDropdown";
import { CandleChart } from "./CandleChart";
// import { calculateProbabilities } from "./CalculateProbability";
import { useLiveMarket } from "../dashboard/UseLivemarket";
import { endpoint } from "../../services/urls";
import { useSocket } from "../../shared/SocketContext";
import { apiConnectorGet, apiConnectorPost } from "../../services/apiconnector";

const COIN_LIST = [
  "BTCUSDT",
  "XRPUSDT",
  "DOGEUSDT",
  "LINKUSDT",
  "SHIBUSDT",
  "ETHUSDT",
  "DASHUSDT",
  "BCHUSDT",
  "FILUSDT",
  "LTCUSDT",
  "YFIUSDT",
  "ZECUSDT",
  "ADAUSDT",
  "DOTUSDT",
  "TRXUSDT",
  "BNBUSDT",
];

const INTERVALS = [
  { label: "1 min", seconds: 60, interval: "1m" },
  { label: "3 min", seconds: 180, interval: "3m" },
  { label: "5 min", seconds: 300, interval: "5m" },
  { label: "15 min", seconds: 900, interval: "15m" },
  // { label: "30 min", seconds: 1800, interval: "30m" },
];

const LINE_OPTIONS = ["Bar", "Line"];
const INDICATOR_OPTIONS = [
  "VOL",
  "MACD",
  "KDJ",
  "RSI",
  "DMI",
  "OBV",
  "BOLL",
  "BRAR",
  "WR",
];

const LEVERAGE_OPTIONS = [1, 2, 5, 10, 20, 50, 100];

const pad = (n) => String(n).padStart(2, "0");

const fmtVol = (v) => {
  if (!v) return "0";
  if (v >= 1000000) return (v / 1000000).toFixed(2) + "M";
  if (v >= 1000) return (v / 1000).toFixed(2) + "K";
  return v.toFixed(2);
};

const fmtCandleTime = (ts) => {
  if (!ts) return "–";
  const d = new Date(ts);
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const fmtPrice = (price) => {
  const n = Number(price);
  if (!n) return "0.00";
  if (n < 0.000001) return n.toFixed(8);
  if (n < 0.001) return n.toFixed(7);
  if (n < 1) return n.toFixed(5);
  if (n > 10000)
    return n.toLocaleString("en-US", {
      minimumFractionDigits: 4,
      maximumFractionDigits: 4,
    });
  if (n > 100) return n.toFixed(3);
  return n.toFixed(5);
};

function CandleInfoBar({ candle, isLive }) {
  if (!candle) return null;
  const bull = candle.close >= candle.open;
  const col = bull ? "#089981" : "#ff5252";
  const items = [
    { label: "O", value: fmtPrice(candle.open), color: col },
    { label: "H", value: fmtPrice(candle.high), color: "#d1d5db" },
    { label: "L", value: fmtPrice(candle.low), color: "#d1d5db" },
    { label: "C", value: fmtPrice(candle.close), color: col },
    {
      label: "V",
      value: fmtVol(candle.vol ?? candle.volume ?? 0),
      color: "#d1d5db",
    },
  ];
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: "10px",
        padding: "4px 8px",
        flexWrap: "wrap",
        fontSize: "11px",
        fontFamily: "monospace",
        borderTop: "1px solid rgba(255,255,255,0.05)",
        minHeight: "26px",
      }}
    >
      {isLive && (
        <span
          style={{
            width: "6px",
            height: "6px",
            borderRadius: "50%",
            background: "#089981",
            flexShrink: 0,
            boxShadow: "0 0 4px #089981",
          }}
        />
      )}
      {items.map(({ label, value, color }) => (
        <span
          key={label}
          style={{ display: "flex", gap: "3px", alignItems: "baseline" }}
        >
          <span style={{ color: "#73777c" }}>{label}:</span>
          <span style={{ color, fontWeight: 500 }}>{value}</span>
        </span>
      ))}
    </div>
  );
}

const calcMA = (candles, period) => {
  if (!candles || candles.length < period) return null;

  let sum = 0;
  for (let i = candles.length - period; i < candles.length; i++) {
    sum += candles[i].close;
  }
  return sum / period;
};

// ── Main ──────────────────────────────────────────────────────────────────────
export default function FuturesTrade({ initialSymbol = "BTCUSDT" }) {
  const [candles, setCandles] = useState([]);
  const { symbol: routeSym } = useParams();
  const navigate = useNavigate();
  const symbol = routeSym || initialSymbol;

  const [drawer, setDrawer] = useState(false);
  const [ivIdx, setIvIdx] = useState(0);
  const [tab, setTab] = useState(0);
  const [chartType, setChartType] = useState("Bar");
  const [indicator, setIndicator] = useState("VOL");
  const [lineOpen, setLineOpen] = useState(false);
  const [volOpen, setVolOpen] = useState(false);

  // ── Trading states ──────────────────────────────────────────────────────
  const [userWallet, setUserWallet] = useState(0);
  const [useRegBonus, setUseRegBonus] = useState(true);
  const [regBonusBalance, setRegBonusBalance] = useState(0);
  const [fundWalletBalance, setFundWalletBalance] = useState(0);
  const [leverage, setLeverage] = useState(1);
  const [lotSize, setLotSize] = useState(10);
  const [activeTrade, setActiveTrade] = useState(null);
  const [tradeResult, setTradeResult] = useState(null);
  const activeTradeRef = useRef(null);
  const prevCandleRef = useRef(0);
  const tradeJustClosedRef = useRef(false);
  const [leverageOpen, setLeverageOpen] = useState(false);
  const [selectedCandle, setSelectedCandle] = useState(null);
  const [isLiveCandle, setIsLiveCandle] = useState(true);
  const handleCandleSelectFromChart = (candle) => {
    if (!candle) {
      setSelectedCandle(null);
      setIsLiveCandle(true);
      return;
    }
    setSelectedCandle(candle);
    setIsLiveCandle(candle._isLive === true);
  };
  const [timer, setTimer] = useState("00:00:00");
  const [candleCountdown, setCandleCountdown] = useState(0);
  const currentPriceRef = useRef(null);

  const ivSec = INTERVALS[ivIdx].seconds;
  const { coins } = useLiveMarket();

  const coin = coins[symbol];
  const socket = useSocket();
  const queryClient = useQueryClient();

  useEffect(() => {
    const handleOneMin = (data) => {
      setTimer(data);
      const parts = String(data).split(":");
      if (parts.length === 3) {
        const secs = Number(parts[2]);
        if (secs === 2) queryClient.invalidateQueries({ queryKey: ["my_trades"] });
        if (secs === 2) queryClient.invalidateQueries({ queryKey: ["wallet"] });
      }
    };
    socket.on("seconds", handleOneMin);
    return () => {
      socket.off("seconds", handleOneMin);
    };
  }, [socket, queryClient]);

  useEffect(() => {
    const parts = String(timer).split(":").map(Number);
    if (parts.length !== 3 || parts.some(isNaN)) return;
    const totalSec = parts[0] * 3600 + parts[1] * 60 + parts[2];
    setCandleCountdown(ivSec - (totalSec % ivSec));
  }, [timer, ivSec]);

  const candlesWithLivePrice = useMemo(() => {
    if (!candles.length || !coin?.lastPrice) return candles;

    const updated = [...candles];
    const lastIdx = updated.length - 1;

    updated[lastIdx] = {
      ...updated[lastIdx],
      close: Number(coin.lastPrice),
    };

    return updated;
  }, [candles, coin?.lastPrice]);

  const change = Number(coin?.priceChangePercent ?? 0);
  const positive = change >= 0;
  const base = symbol.replace("USDT", "");

  const ma7 = useMemo(
    () => calcMA(candlesWithLivePrice, 7),
    [candlesWithLivePrice],
  );
  const ma25 = useMemo(
    () => calcMA(candlesWithLivePrice, 25),
    [candlesWithLivePrice],
  );
  const ma99 = useMemo(
    () => calcMA(candlesWithLivePrice, 99),
    [candlesWithLivePrice],
  );

  const { bullPct, isBull, estProfit } = useMemo(() => {
    const last5 = candlesWithLivePrice.slice(-5);
    if (!last5.length) return { bullPct: 50, isBull: true, estProfit: 0 };
    const greenCount = last5.filter((c) => c.close >= c.open).length;
    const pct = Math.round((greenCount / last5.length) * 100);
    const avgMove =
      last5.reduce((sum, c) => sum + Math.abs(c.close - c.open), 0) /
      last5.length;
    const position = (parseFloat(lotSize) || 0) * leverage;
    const price = Number(coin?.lastPrice) || 1;
    const profit = price > 0 ? (position / price) * avgMove : 0;
    return { bullPct: pct, isBull: pct >= 50, estProfit: profit };
  }, [candlesWithLivePrice, lotSize, leverage, coin?.lastPrice]);

  // Keep latest price in a ref so the timer closure is never stale
  useEffect(() => {
    if (coin?.lastPrice) currentPriceRef.current = Number(coin.lastPrice);
  }, [coin?.lastPrice]);

  const [pendingOrder, setPendingOrder] = useState(null);
  const [orderSuccess, setOrderSuccess] = useState(null);

  const { data: apiTrades = [], isLoading: tradesLoading } = useQuery({
    queryKey: ["my_trades"],
    queryFn: async () => {
      const res = await apiConnectorPost(endpoint.my_trades, { page: 1 });
      const raw = res?.data;
      const rows = Array.isArray(raw?.result?.data)
        ? raw.result.data
        : Array.isArray(raw?.result)
          ? raw.result
          : Array.isArray(raw?.data)
            ? raw.data
            : Array.isArray(raw)
              ? raw
              : [];
      return rows.map((row, i) => ({
        id: row.tr15_id ?? i,
        pair: row.tr15_pair ?? "",
        tradeType: row.tr15_trade_type ?? "",
        entryPrice: Number(row.tr15_current_price ?? 0),
        exitPrice: Number(row.tr15_exit_price ?? 0),
        lotSize: Number(row.tr15_lot_size ?? 0),
        leverage: Number(row.tr15_leverage ?? 1),
        timeFrame: Number(row.tr15_time_frame ?? 1),
        status: row.tr15_trade_staus ?? row.tr15_trade_status ?? "Pending",
        profit: Number(row.tr15_profit ?? 0),
        entryTime: row.tr15_entry_time ? new Date(row.tr15_entry_time) : null,
        exitTime: row.tr15_exit_time ? new Date(row.tr15_exit_time) : null,
      }));
    },
    staleTime: 62000,
  });

  // Keep activeTradeRef in sync so the candle-reset effect avoids stale closures
  useEffect(() => {
    activeTradeRef.current = activeTrade;
  }, [activeTrade]);

  useEffect(() => {
    const pending = apiTrades.find((t) => t.status === "Pending");

    if (pending) {
      setActiveTrade((prev) => prev ?? {
        type: pending.tradeType.toLowerCase(),
        entryPrice: pending.entryPrice,
        amount: pending.lotSize,
        leverage: pending.leverage,
        pair: pending.pair,
      });
    } else {
      // No pending trade in DB → clear active trade (handles trade close)
      setActiveTrade(null);
    }
  }, [apiTrades]);

  // Detect candle reset (countdown jumps from near-0 back up) and close active trade
  // useEffect(() => {
  //   if (prevCandleRef.current <= 2 && candleCountdown > 10) {
  //     if (activeTradeRef.current) {
  //       tradeJustClosedRef.current = true;
  //       setActiveTrade(null);
  //       setTradeResult(null);
  //       setTimeout(() => {
  //         queryClient.invalidateQueries({ queryKey: ["my_trades"] });
  //         tradeJustClosedRef.current = false;
  //       }, 2000);
  //     }
  //   }
  //   prevCandleRef.current = candleCountdown;
  // }, [candleCountdown, queryClient]);

  useEffect(() => {
    if (candleCountdown <= 10) setPendingOrder(null);
  }, [candleCountdown]);

  const { data: userData } = useQuery({
    queryKey: ["wallet"],
    queryFn: () => apiConnectorGet(endpoint.member_details),
    enabled: !!endpoint?.member_details,
    refetchOnMount: false,
    refetchOnWindowFocus: true,
  });

  useEffect(() => {
    const member = userData?.data?.result?.[0];
    const regBonus = parseFloat(member?.tr03_reg_bonus ?? 0);
    const fundWallet = parseFloat(member?.tr03_topup_wallet ?? 0);
    setRegBonusBalance(regBonus);
    setFundWalletBalance(fundWallet);
    setUserWallet(useRegBonus ? regBonus : fundWallet);
  }, [userData, useRegBonus]);

  const requestTrade = (type) => {
    const amount = parseFloat(lotSize) || 0;
    if (amount <= 0) return;
    const entryPrice = currentPriceRef.current ?? Number(coin?.lastPrice);
    if (!entryPrice) return;
    setPendingOrder({
      type,
      amount,
      entryPrice,
      leverage,
      timeframe: ivSec / 60,
    });
  };

  const confirmTrade = async () => {
    if (!pendingOrder || candleCountdown <= 10) return;
    const { type, amount, entryPrice, leverage: lev, timeframe } = pendingOrder;
    setPendingOrder(null);
    try {
      const res = await apiConnectorPost(endpoint.place_trade_order, {
        trade_pair: symbol,
        trade_type: type.toUpperCase(),
        current_price: entryPrice,
        lot_size: amount,
        leverage: lev,
        time_frame: timeframe,
        wallet_type: useRegBonus ? "reg_bonus" : "fund_wallet",
      });
      const message =
        res?.data?.message || res?.data?.msg || "Order placed successfully";

      setUserWallet((prev) => prev - amount);
      setActiveTrade({ type, entryPrice, amount, leverage: lev, pair: symbol });
      setTradeResult(null);
      setOrderSuccess(message);
      setTimeout(() => setOrderSuccess(null), 2000);
      queryClient.invalidateQueries({ queryKey: ["my_trades"] });
      queryClient.invalidateQueries({ queryKey: ["wallet"] });
    } catch (err) {
      console.error("Failed to place order:", err);
    }
  };

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        backgroundColor: "#0d1117",
        minHeight: "100dvh",
        overflowY: "auto",
      }}
    >
      {/* Header */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "12px 16px",
          flexShrink: 0,
          borderBottom: "1px solid rgba(255,255,255,0.06)",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "12px",
            position: "relative",
          }}
        >
          <div style={{ position: "relative" }}>
            <button
              onClick={() => setDrawer(!drawer)}
              style={{
                background: "none",
                border: "none",
                color: "#9ca3af",
                cursor: "pointer",
              }}
            >
              <svg
                width="20"
                height="20"
                fill="none"
                stroke="currentColor"
                strokeWidth={2}
                viewBox="0 0 24 24"
              >
                <path
                  d="M4 6h16M4 12h10M4 18h16"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>

            {drawer && (
              <>
                <div
                  onClick={() => setDrawer(false)}
                  style={{
                    position: "fixed",
                    inset: 0,
                    zIndex: 40,
                  }}
                />

                <div
                  style={{
                    position: "absolute",
                    left: "-16px",
                    top: "36px",
                    zIndex: 50,
                    display: "flex",
                    flexDirection: "column",
                    overflowY: "auto",
                    borderRadius: "12px",
                    background: "#131920",
                    border: "1px solid rgba(255,255,255,0.08)",
                    width: "180px",
                    maxHeight: "82vh",
                    boxShadow: "0 8px 32px rgba(0,0,0,0.6)",
                  }}
                >
                  <div
                    style={{
                      padding: "10px 16px",
                      position: "sticky",
                      top: 0,
                      background: "#131920",
                      borderBottom: "1px solid rgba(255,255,255,0.06)",
                    }}
                  >
                    <span
                      style={{
                        color: "#ffffff",
                        fontWeight: "bold",
                        fontSize: "13px",
                        letterSpacing: "0.5px",
                      }}
                    >
                      Trade
                    </span>
                  </div>

                  {COIN_LIST.map((sym) => {
                    const active = sym === symbol;
                    const b = sym.replace("USDT", "");

                    return (
                      <button
                        key={sym}
                        onClick={() => {
                          navigate(`/binary/${sym}`);
                          setDrawer(false);
                        }}
                        style={{
                          width: "100%",
                          textAlign: "left",
                          padding: "12px 16px",
                          borderBottom: "1px solid rgba(255,255,255,0.04)",
                          background: active
                            ? "rgba(255,255,255,0.1)"
                            : "transparent",
                          border: "none",
                          cursor: "pointer",
                        }}
                      >
                        <span
                          style={{
                            fontWeight: "bold",
                            fontSize: "14px",
                            color: active ? "#ffffff" : "#d1d5db",
                          }}
                        >
                          {b}
                        </span>

                        <span
                          style={{
                            color: "#6b7280",
                            fontWeight: "normal",
                            fontSize: "12px",
                            marginLeft: "4px",
                          }}
                        >
                          / USDT
                        </span>
                      </button>
                    );
                  })}
                </div>
              </>
            )}
          </div>

          <span
            style={{
              color: "#ffffff",
              fontWeight: "bold",
              fontSize: "16px",
            }}
          >
            {base} / USDT
          </span>

          <span
            style={{
              fontWeight: "bold",
              fontSize: "14px",
              color: positive ? "#089981" : "#ff5252",
            }}
          >
            {positive ? "+" : ""}
            {change.toFixed(2)}%
          </span>
        </div>

        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "16px",
          }}
        >
          <button
            style={{
              background: "none",
              border: "none",
              color: "#9ca3af",
              cursor: "pointer",
            }}
          >
            <svg
              width="20"
              height="20"
              fill="none"
              stroke="currentColor"
              strokeWidth={1.5}
              viewBox="0 0 24 24"
            >
              <circle cx="12" cy="12" r="10" />
              <path
                d="M12 16v-4M12 8h.01"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>

          <TrendDropdown
            symbol={symbol}
            interval={INTERVALS[ivIdx].interval}
            onData={setCandles}
          />
        </div>
      </div>

      {/* Interval tabs */}
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          gap: "8px",
          padding: "10px 16px",
          flexShrink: 0,
        }}
      >
        {INTERVALS.map((iv, i) => (
          <button
            key={iv.label}
            onClick={() => setIvIdx(i)}
            style={{
              padding: "6px 20px",
              marginLeft: "5px",
              borderRadius: "6px",
              fontSize: "13px",
              fontWeight: "bold",
              transition: "all 0.2s ease",
              backgroundColor: ivIdx === i ? "#287dd1" : "#1a2030",
              color: ivIdx === i ? "#ffffff" : "#6b7280",
              border: "none",
              cursor: "pointer",
            }}
          >
            {iv.label}
          </button>
        ))}
      </div>

      {/* ── Price Header (add this just above the Chart section) ── */}
      <div
        style={{
          padding: "0 12px 12px 8px",
          flexShrink: 0,
        }}
      >
        {/* Top row: price + 24h stats */}
        <div
          style={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            marginBottom: "4px",
          }}
        >
          {/* Left: current price */}
          <div>
            <div
              style={{
                fontSize: "22px",
                fontWeight: "bold",
                color: positive ? "#089981" : "#ff5252",
                lineHeight: 1.1,
              }}
            >
              {coin?.lastPrice
                ? Number(coin.lastPrice).toLocaleString("en-US", {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 5,
                  })
                : "—"}
            </div>
            <div
              style={{
                fontSize: "12px",
                color: positive ? "#089981" : "#ff5252",
                marginTop: "2px",
              }}
            >
              $
              {coin?.lastPrice
                ? Number(coin.lastPrice).toLocaleString("en-US", {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 5,
                  })
                : "—"}{" "}
              {positive ? "+" : ""}
              {change.toFixed(2)}%
            </div>
          </div>

          {/* Right: 24h stats grid */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: "2px 16px",
              textAlign: "right",
            }}
          >
            {[
              {
                label: "24h High",
                value: coin?.highPrice
                  ? Number(coin.highPrice).toLocaleString()
                  : "—",
              },
              {
                label: "24h Low",
                value: coin?.lowPrice
                  ? Number(coin.lowPrice).toLocaleString()
                  : "—",
              },
              {
                label: `24h Vol(${base})`,
                value: coin?.volume ? Number(coin.volume).toFixed(2) : "—",
              },
              {
                label: "24h Vol(USDT)",
                value: coin?.quoteVolume
                  ? (Number(coin.quoteVolume) / 1e9).toFixed(4) + "B"
                  : "—",
              },
            ].map(({ label, value }) => (
              <div key={label}>
                <div style={{ fontSize: "10px", color: "#6b7280" }}>
                  {label}
                </div>
                <div
                  style={{
                    fontSize: "11px",
                    color: "#d1d5db",
                    fontWeight: 500,
                  }}
                >
                  {value}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* MA indicator row */}
        <div
          style={{
            display: "flex",
            gap: "10px",
            fontSize: "11px",
            color: "#6b7280",
            flexWrap: "wrap",
          }}
        >
          <span>
            MA(7){" "}
            <span style={{ color: "#e6b422" }}>
              {ma7 ? ma7.toFixed(2) : "—"}
            </span>
          </span>

          <span>
            MA(25){" "}
            <span style={{ color: "#e84d68" }}>
              {ma25 ? ma25.toFixed(2) : "—"}
            </span>
          </span>

          <span>
            MA(99){" "}
            <span style={{ color: "#a78bfa" }}>
              {ma99 ? ma99.toFixed(2) : "—"}
            </span>
          </span>
        </div>
      </div>
      {/* Chart type row */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "16px",
          padding: "0 16px 4px 16px",
          flexShrink: 0,
          position: "relative",
        }}
      >
        {/* Line Type Dropdown */}
        <div style={{ position: "relative" }}>
          <button
            onClick={() => {
              setLineOpen(!lineOpen);
              setVolOpen(false);
            }}
            style={{
              display: "flex",
              alignItems: "center",
              gap: "2px",
              fontSize: "12px",
              color: "#d1d5db",
              background: "none",
              border: "none",
              cursor: "pointer",
            }}
          >
            {chartType}
            <svg width="12" height="12" fill="currentColor" viewBox="0 0 20 20">
              <path d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z" />
            </svg>
          </button>

          {lineOpen && (
            <>
              <div
                onClick={() => setLineOpen(false)}
                style={{
                  position: "fixed",
                  inset: 0,
                  zIndex: 40,
                }}
              />

              <div
                style={{
                  position: "absolute",
                  left: 0,
                  top: "28px",
                  zIndex: 50,
                  borderRadius: "12px",
                  overflow: "hidden",
                  background: "#131920",
                  border: "1px solid rgba(255,255,255,0.08)",
                  width: "110px",
                  boxShadow: "0 8px 32px rgba(0,0,0,0.7)",
                }}
              >
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(2, 1fr)",
                    gap: 0,
                  }}
                >
                  {LINE_OPTIONS.map((opt) => (
                    <button
                      key={opt}
                      onClick={() => {
                        setChartType(opt);
                        setLineOpen(false);
                      }}
                      style={{
                        padding: "8px 0",
                        fontSize: "13px",
                        fontWeight: chartType === opt ? "bold" : "500",
                        color: chartType === opt ? "#ffffff" : "#9ca3af",
                        background:
                          chartType === opt
                            ? "rgba(255,255,255,0.08)"
                            : "transparent",
                        borderBottom: "1px solid rgba(255,255,255,0.05)",
                        borderRight: "1px solid rgba(255,255,255,0.05)",
                        textAlign: "center",
                        cursor: "pointer",
                        transition: "color 0.2s ease, background 0.2s ease",
                      }}
                    >
                      {opt}
                    </button>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>

        {/* Indicator Dropdown */}
        <div style={{ position: "relative" }}>
          <button
            onClick={() => {
              setVolOpen(!volOpen);
              setLineOpen(false);
            }}
            style={{
              display: "flex",
              alignItems: "center",
              gap: "2px",
              fontSize: "12px",
              color: "#d1d5db",
              background: "none",
              border: "none",
              cursor: "pointer",
            }}
          >
            {indicator}
            <svg width="12" height="12" fill="currentColor" viewBox="0 0 20 20">
              <path d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z" />
            </svg>
          </button>

          {volOpen && (
            <>
              <div
                onClick={() => setVolOpen(false)}
                style={{
                  position: "fixed",
                  inset: 0,
                  zIndex: 40,
                }}
              />

              <div
                style={{
                  position: "absolute",
                  left: 0,
                  top: "28px",
                  zIndex: 50,
                  borderRadius: "12px",
                  overflow: "hidden",
                  background: "#131920",
                  border: "1px solid rgba(255,255,255,0.08)",
                  width: "280px",
                  boxShadow: "0 8px 32px rgba(0,0,0,0.7)",
                }}
              >
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(5, 1fr)",
                    gap: 0,
                  }}
                >
                  {INDICATOR_OPTIONS.map((opt) => (
                    <button
                      key={opt}
                      onClick={() => {
                        setIndicator(opt);
                        setVolOpen(false);
                      }}
                      style={{
                        padding: "12px 0",
                        fontSize: "13px",
                        fontWeight: indicator === opt ? "bold" : "500",
                        color: indicator === opt ? "#ffffff" : "#9ca3af",
                        background:
                          indicator === opt
                            ? "rgba(255,255,255,0.08)"
                            : "transparent",
                        borderBottom: "1px solid rgba(255,255,255,0.05)",
                        borderRight: "1px solid rgba(255,255,255,0.05)",
                        textAlign: "center",
                        cursor: "pointer",
                        transition: "color 0.2s ease, background 0.2s ease",
                      }}
                    >
                      {opt}
                    </button>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      <CandleInfoBar candle={selectedCandle} isLive={isLiveCandle} />

      {/* Chart */}
      <div
        style={{
          flexShrink: 0,
          padding: "0 10px",
          height: "36vh",
          minHeight: "200px",
        }}
      >
        <CandleChart
          symbol={symbol}
          interval={INTERVALS[ivIdx].interval}
          chartType={chartType}
          indicator={indicator}
          onData={setCandles}
          onCandleSelect={handleCandleSelectFromChart}
        />
      </div>

      {/* ── Trading Panel ──────────────────────────────────────────────── */}
      <div
        style={{
          flexShrink: 0,
          background: "#10161f",
          borderTop: "1px solid rgba(255,215,0,0.12)",
        }}
      >
        {/* Server timer + balance strip */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "10px 16px",
            background: "rgba(255,255,255,0.02)",
            borderBottom: "1px solid rgba(255,255,255,0.05)",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <div
              style={{
                width: "7px",
                height: "7px",
                borderRadius: "50%",
                background: candleCountdown <= 10 ? "#f5a623" : "#00b578",
                boxShadow: `0 0 8px ${candleCountdown <= 10 ? "#f5a623" : "#00b578"}`,
                flexShrink: 0,
              }}
            />
            <span
              style={{
                fontSize: "10px",
                color: "#6b7280",
                letterSpacing: "1px",
                textTransform: "uppercase",
              }}
            >
              Candle Closes In
            </span>
            <span
              style={{
                fontSize: "17px",
                fontWeight: "bold",
                color: candleCountdown <= 10 ? "#f5a623" : "#ffffff",
                fontVariantNumeric: "tabular-nums",
                letterSpacing: "2px",
              }}
            >
              {`${String(Math.floor(candleCountdown / 60)).padStart(2, "0")}:${String(candleCountdown % 60).padStart(2, "0")}`}
            </span>
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
            <span style={{ fontSize: "10px", color: "#6b7280" }}>
              {useRegBonus ? "Reg Bonus" : "Trade Wallet"}
            </span>
            <span
              style={{ fontSize: "15px", fontWeight: "bold", color: "#f5a623" }}
            >
              {userWallet}
              <span
                style={{
                  fontSize: "10px",
                  color: "#9ca3af",
                  marginLeft: "3px",
                }}
              >
                USDT
              </span>
            </span>
          </div>
        </div>

        {/* Active trade strip */}
        {activeTrade &&
          (() => {
            const livePrice = Number(coin?.lastPrice) || 0;
            const direction = activeTrade.type === "call" ? 1 : -1;
            const movePct =
              livePrice > 0 && activeTrade.entryPrice > 0
                ? (livePrice - activeTrade.entryPrice) / activeTrade.entryPrice
                : 0;
            const rawPnl =
              direction * movePct * activeTrade.amount * activeTrade.leverage;
            const pnl = Math.max(
              -activeTrade.amount,
              Math.min(activeTrade.amount * activeTrade.leverage, rawPnl),
            );
            const isWinning = pnl >= 0;
            return (
              <div
                style={{
                  padding: "8px 16px",
                  background:
                    activeTrade.type === "call"
                      ? "rgba(0,181,120,0.07)"
                      : "rgba(255,82,82,0.07)",
                  borderBottom: "1px solid rgba(255,255,255,0.04)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <div
                  style={{ display: "flex", alignItems: "center", gap: "8px" }}
                >
                  <span
                    style={{
                      padding: "2px 10px",
                      borderRadius: "20px",
                      fontSize: "11px",
                      fontWeight: "bold",
                      color: "#fff",
                      background:
                        activeTrade.type === "call" ? "#00b578" : "#ff5252",
                    }}
                  >
                    {activeTrade.type === "call" ? "↑ CALL" : "↓ PUT"}
                  </span>
                  <span
                    style={{
                      fontSize: "11px",
                      color: "#6b7280",
                      fontVariantNumeric: "tabular-nums",
                    }}
                  >
                    ⏱ {candleCountdown}s
                  </span>
                </div>
                <span
                  style={{
                    fontSize: "13px",
                    fontWeight: "bold",
                    color: isWinning ? "#00b578" : "#ff5252",
                    fontVariantNumeric: "tabular-nums",
                  }}
                >
                  {isWinning ? "+" : ""}
                  {pnl.toFixed(2)} USDT
                </span>
              </div>
            );
          })()}

        {/* Controls */}
        <div style={{ padding: "12px 16px 10px" }}>
          {/* Wallet selector */}
          <div style={{ marginBottom: "10px" }}>
            <div
              style={{
                fontSize: "10px",
                color: "#6b7280",
                marginBottom: "5px",
                letterSpacing: "0.5px",
                textTransform: "uppercase",
              }}
            >
              Select Wallet
            </div>
            <div
              style={{
                display: "flex",
                borderRadius: "10px",
                overflow: "hidden",
                border: "1px solid rgba(255,215,0,0.18)",
                background: "#0d1117",
              }}
            >
              <button
                onClick={() => setUseRegBonus(true)}
                style={{
                  flex: 1,
                  padding: "10px 0",
                  fontSize: "12px",
                  fontWeight: "bold",
                  color: useRegBonus ? "#0d1117" : "#f5a623",
                  background: useRegBonus
                    ? "linear-gradient(135deg, #f5a623, #e6b422)"
                    : "transparent",
                  border: "none",
                  cursor: "pointer",
                  transition: "all 0.2s",
                  lineHeight: 1.4,
                }}
              >
                Reg Bonus
                <div
                  style={{
                    fontSize: "10px",
                    fontWeight: "normal",
                    color: useRegBonus ? "#0d1117" : "#9ca3af",
                    marginTop: "2px",
                  }}
                >
                  {regBonusBalance.toFixed(2)} USDT
                </div>
              </button>
              <button
                onClick={() => setUseRegBonus(false)}
                style={{
                  flex: 1,
                  padding: "10px 0",
                  fontSize: "12px",
                  fontWeight: "bold",
                  color: !useRegBonus ? "#0d1117" : "#f5a623",
                  background: !useRegBonus
                    ? "linear-gradient(135deg, #f5a623, #e6b422)"
                    : "transparent",
                  border: "none",
                  borderLeft: "1px solid rgba(255,215,0,0.18)",
                  cursor: "pointer",
                  transition: "all 0.2s",
                  lineHeight: 1.4,
                }}
              >
                Fund Wallet
                <div
                  style={{
                    fontSize: "10px",
                    fontWeight: "normal",
                    color: !useRegBonus ? "#0d1117" : "#9ca3af",
                    marginTop: "2px",
                  }}
                >
                  {fundWalletBalance.toFixed(2)} USDT
                </div>
              </button>
            </div>
          </div>

          {/* Lot size + Leverage */}
          <div style={{ display: "flex", gap: "10px", marginBottom: "10px" }}>
            {/* Lot size */}
            <div style={{ flex: 1 }}>
              <div
                style={{
                  fontSize: "10px",
                  color: "#6b7280",
                  marginBottom: "5px",
                  letterSpacing: "0.5px",
                  textTransform: "uppercase",
                }}
              >
                Lot Size (USDT)
              </div>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  background: "#0d1117",
                  borderRadius: "10px",
                  border: "1px solid rgba(255,215,0,0.18)",
                  overflow: "hidden",
                  height: "42px",
                }}
              >
                <button
                  onClick={() =>
                    setLotSize((prev) =>
                      Math.max(1, (parseFloat(prev) || 0) - 1),
                    )
                  }
                  style={{
                    width: "40px",
                    height: "100%",
                    background: "rgba(245,166,35,0.06)",
                    border: "none",
                    borderRight: "1px solid rgba(255,215,0,0.12)",
                    color: "#f5a623",
                    fontSize: "20px",
                    cursor: "pointer",
                    flexShrink: 0,
                  }}
                >
                  −
                </button>
                <input
                  type="number"
                  value={lotSize}
                  onChange={(e) => setLotSize(e.target.value)}
                  min={1}
                  style={{
                    flex: 1,
                    textAlign: "center",
                    background: "none",
                    border: "none",
                    color: "#ffffff",
                    fontSize: "14px",
                    fontWeight: "bold",
                    outline: "none",
                    minWidth: 0,
                  }}
                />
                <button
                  onClick={() =>
                    setLotSize((prev) => (parseFloat(prev) || 0) + 1)
                  }
                  style={{
                    width: "40px",
                    height: "100%",
                    background: "rgba(245,166,35,0.06)",
                    border: "none",
                    borderLeft: "1px solid rgba(255,215,0,0.12)",
                    color: "#f5a623",
                    fontSize: "20px",
                    cursor: "pointer",
                    flexShrink: 0,
                  }}
                >
                  +
                </button>
              </div>
            </div>

            {/* Leverage */}
            <div style={{ flex: 1 }}>
              <div
                style={{
                  fontSize: "10px",
                  color: "#6b7280",
                  marginBottom: "5px",
                  letterSpacing: "0.5px",
                  textTransform: "uppercase",
                }}
              >
                Leverage
              </div>
              <div style={{ position: "relative" }}>
                <button
                  onClick={() => setLeverageOpen(!leverageOpen)}
                  style={{
                    width: "100%",
                    height: "42px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    padding: "0 14px",
                    borderRadius: "10px",
                    background: "#0d1117",
                    border: "1px solid rgba(255,215,0,0.18)",
                    color: "#f5a623",
                    fontSize: "14px",
                    fontWeight: "bold",
                    cursor: "pointer",
                  }}
                >
                  {leverage}x
                  <svg
                    width="12"
                    height="12"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z" />
                  </svg>
                </button>

                {leverageOpen && (
                  <>
                    <div
                      onClick={() => setLeverageOpen(false)}
                      style={{ position: "fixed", inset: 0, zIndex: 40 }}
                    />
                    <div
                      style={{
                        position: "absolute",
                        bottom: "110%",
                        right: 0,
                        zIndex: 50,
                        background: "#131920",
                        borderRadius: "12px",
                        border: "1px solid rgba(255,215,0,0.2)",
                        boxShadow: "0 -8px 32px rgba(0,0,0,0.7)",
                        padding: "8px",
                        display: "grid",
                        gridTemplateColumns: "repeat(4, 1fr)",
                        gap: "6px",
                        width: "200px",
                      }}
                    >
                      {LEVERAGE_OPTIONS.map((lev) => (
                        <button
                          key={lev}
                          onClick={() => {
                            setLeverage(lev);
                            setLeverageOpen(false);
                          }}
                          style={{
                            padding: "8px 0",
                            borderRadius: "6px",
                            fontSize: "13px",
                            fontWeight: "bold",
                            color: leverage === lev ? "#0d1117" : "#f5a623",
                            background:
                              leverage === lev
                                ? "linear-gradient(135deg, #f5a623, #e6b422)"
                                : "rgba(245,166,35,0.1)",
                            border: "1px solid rgba(245,166,35,0.3)",
                            cursor: "pointer",
                          }}
                        >
                          {lev}x
                        </button>
                      ))}
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Stats card */}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "9px 14px",
              borderRadius: "10px",
              background: "rgba(255,255,255,0.03)",
              border: "1px solid rgba(255,255,255,0.06)",
            }}
          >
            <div style={{ textAlign: "center" }}>
              <div
                style={{
                  fontSize: "10px",
                  color: "#6b7280",
                  marginBottom: "3px",
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                }}
              >
                Position
              </div>
              <div
                style={{
                  fontSize: "12px",
                  color: "#d1d5db",
                  fontWeight: "bold",
                }}
              >
                {((parseFloat(lotSize) || 0) * leverage).toFixed(2)}
                <span style={{ color: "#6b7280", fontWeight: "normal" }}>
                  {" "}
                  USDT
                </span>
              </div>
            </div>
            <div
              style={{
                width: "1px",
                alignSelf: "stretch",
                background: "rgba(255,255,255,0.07)",
              }}
            />
            <div style={{ textAlign: "center" }}>
              <div
                style={{
                  fontSize: "10px",
                  color: "#6b7280",
                  marginBottom: "3px",
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                }}
              >
                Signal
              </div>
              <div
                style={{
                  fontSize: "12px",
                  color: isBull ? "#00b578" : "#ff5252",
                  fontWeight: "bold",
                }}
              >
                {isBull ? "▲" : "▼"} {isBull ? bullPct : 100 - bullPct}%
              </div>
            </div>
            <div
              style={{
                width: "1px",
                alignSelf: "stretch",
                background: "rgba(255,255,255,0.07)",
              }}
            />
            <div style={{ textAlign: "center" }}>
              <div
                style={{
                  fontSize: "10px",
                  color: "#6b7280",
                  marginBottom: "3px",
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                }}
              >
                Est. Profit
              </div>
              <div
                style={{
                  fontSize: "12px",
                  color: "#00b578",
                  fontWeight: "bold",
                }}
              >
                +{estProfit.toFixed(2)}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── CALL / PUT Buttons ──────────────────────────────────────────── */}
      <div style={{ flexShrink: 0 }}>
        <div style={{ display: "flex" }}>
          {(() => {
            const lotAmt = parseFloat(lotSize) || 0;
            const insufficient = lotAmt > 0 && lotAmt > userWallet;
            return (
              <>
                <button
                  onClick={() => requestTrade("call")}
                  disabled={insufficient}
                  style={{
                    flex: 1,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "10px",
                    padding: "18px 0",
                    fontSize: "16px",
                    fontWeight: "bold",
                    letterSpacing: "2px",
                    color: "#ffffff",
                    background: insufficient
                      ? "rgba(0,181,120,0.25)"
                      : "linear-gradient(135deg, #00b578, #089981)",
                    border: "none",
                    cursor: insufficient ? "not-allowed" : "pointer",
                    opacity: insufficient ? 0.5 : 1,
                  }}
                >
                  <svg
                    width="18"
                    height="18"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth={3}
                    viewBox="0 0 24 24"
                  >
                    <path
                      d="M5 17l5-5 4 4 5-6"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  CALL
                </button>

                <div
                  style={{
                    width: "1px",
                    background: "rgba(0,0,0,0.4)",
                    flexShrink: 0,
                  }}
                />

                <button
                  onClick={() => requestTrade("put")}
                  disabled={insufficient}
                  style={{
                    flex: 1,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "10px",
                    padding: "18px 0",
                    fontSize: "16px",
                    fontWeight: "bold",
                    letterSpacing: "2px",
                    color: "#ffffff",
                    background: insufficient
                      ? "rgba(255,82,82,0.25)"
                      : "linear-gradient(135deg, #ff5252, #e03030)",
                    border: "none",
                    cursor: insufficient ? "not-allowed" : "pointer",
                    opacity: insufficient ? 0.5 : 1,
                  }}
                >
                  PUT
                  <svg
                    width="18"
                    height="18"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth={3}
                    viewBox="0 0 24 24"
                  >
                    <path
                      d="M5 7l5 5 4-4 5 6"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>
              </>
            );
          })()}
        </div>
      </div>

      {/* Bottom tabs */}
      <div
        style={{
          flexShrink: 0,
          borderTop: "1px solid rgba(255,255,255,0.06)",
          paddingBottom: "80px",
        }}
      >
        {/* Tabs */}
        <div
          style={{
            display: "flex",
            overflowX: "auto",
            padding: "10px 16px 10px 16px",
            gap: "4px",
            scrollbarWidth: "none",
          }}
        >
          {[
            "Position order",
            "Historical orders",
            "Invited me",
            "Follow-up",
          ].map((t, i) => (
            <button
              key={t}
              onClick={() => setTab(i)}
              style={{
                whiteSpace: "nowrap",
                fontSize: "13px",
                fontWeight: 500,
                paddingBottom: "8px",
                paddingLeft: "4px",
                paddingRight: "4px",
                marginRight: "12px",
                transition: "color 0.2s ease, border-bottom 0.2s ease",
                flexShrink: 0,
                color: tab === i ? "#ffffff" : "#6b7280",
                borderBottom:
                  tab === i ? "2px solid #f5a623" : "2px solid transparent",
                background: "none",
                border: "none",
                cursor: "pointer",
              }}
            >
              {t}
            </button>
          ))}
        </div>

        {/* Tab content */}
        {tab === 0 && (
          <div
            style={{
              padding: "10px 16px 16px",
              display: "flex",
              flexDirection: "column",
              gap: "8px",
            }}
          >
            {(() => {
              const pendingTrades = apiTrades.filter(
                (t) => t.status === "Pending"
              );
              if (pendingTrades.length === 0) {
                return (
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      padding: "20px 0",
                      color: "#9ca3af",
                    }}
                  >
                    <svg
                      width="36"
                      height="36"
                      fill="currentColor"
                      style={{ opacity: 0.3, marginBottom: "4px" }}
                      viewBox="0 0 24 24"
                    >
                      <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                    </svg>
                    <span style={{ fontSize: "11px" }}>No active orders</span>
                  </div>
                );
              }
              return pendingTrades.map((t) => {
                const isCall = t.tradeType.toUpperCase() === "CALL";
                return (
                  <div
                    key={t.id}
                    style={{
                      background: "rgba(255,255,255,0.03)",
                      borderRadius: "12px",
                      border: `1px solid ${isCall ? "rgba(0,181,120,0.25)" : "rgba(255,82,82,0.25)"}`,
                      padding: "12px 14px",
                      display: "flex",
                      flexDirection: "column",
                      gap: "10px",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                        }}
                      >
                        <span
                          style={{
                            padding: "2px 10px",
                            borderRadius: "20px",
                            fontSize: "11px",
                            fontWeight: "bold",
                            color: "#fff",
                            background: isCall ? "#00b578" : "#ff5252",
                          }}
                        >
                          {isCall ? "↑ CALL" : "↓ PUT"}
                        </span>
                        <span
                          style={{
                            fontSize: "12px",
                            color: "#d1d5db",
                            fontWeight: "bold",
                          }}
                        >
                          {(t.pair || symbol).replace("USDT", "")} / USDT
                        </span>
                        <span
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "4px",
                            fontSize: "10px",
                            color: "#00b578",
                          }}
                        >
                          <span
                            style={{
                              width: "5px",
                              height: "5px",
                              borderRadius: "50%",
                              background: "#00b578",
                              boxShadow: "0 0 5px #00b578",
                              display: "inline-block",
                            }}
                          />
                          RUNNING
                        </span>
                      </div>
                      <span
                        style={{
                          fontSize: "13px",
                          fontWeight: "bold",
                          color: "#f5a623",
                          fontVariantNumeric: "tabular-nums",
                        }}
                      >
                        ⏱ {candleCountdown}s
                      </span>
                    </div>
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "repeat(4, 1fr)",
                        gap: "6px",
                        borderTop: "1px solid rgba(255,255,255,0.06)",
                        paddingTop: "10px",
                      }}
                    >
                      {[
                        {
                          label: "Entry",
                          value: t.entryPrice.toLocaleString("en-US", {
                            minimumFractionDigits: 4,
                            maximumFractionDigits: 5,
                          }),
                          color: "#f5a623",
                        },
                        { label: "Lot", value: `${t.lotSize} USDT` },
                        { label: "Leverage", value: `${t.leverage}x` },
                        {
                          label: "Position",
                          value: `${(t.lotSize * t.leverage).toFixed(4)} USDT`,
                        },
                      ].map(({ label, value, color }) => (
                        <div key={label} style={{ textAlign: "center" }}>
                          <div
                            style={{
                              fontSize: "9px",
                              color: "#6b7280",
                              textTransform: "uppercase",
                              letterSpacing: "0.5px",
                              marginBottom: "2px",
                            }}
                          >
                            {label}
                          </div>
                          <div
                            style={{
                              fontSize: "11px",
                              color: color || "#d1d5db",
                              fontWeight: "bold",
                            }}
                          >
                            {value}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              });
            })()}
          </div>
        )}

        {tab === 1 &&
          (() => {
            const closed = apiTrades
              .filter((t) => t.status !== "Pending")
              .slice(0, 5);
            return (
              <div
                style={{
                  padding: "10px 16px 16px",
                  display: "flex",
                  flexDirection: "column",
                  gap: "8px",
                }}
              >
                {tradesLoading ? (
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "center",
                      padding: "20px 0",
                    }}
                  >
                    <div
                      style={{
                        width: "20px",
                        height: "20px",
                        borderRadius: "50%",
                        border: "2px solid rgba(255,255,255,0.1)",
                        borderTopColor: "#f5a623",
                        animation: "spin 0.8s linear infinite",
                      }}
                    />
                  </div>
                ) : closed.length > 0 ? (
                  closed.map((trade) => {
                    const isWin = trade.profit > 0;
                    const fmtP = (n) =>
                      n >= 1000
                        ? n.toLocaleString("en-US", {
                            minimumFractionDigits: 4,
                            maximumFractionDigits: 4,
                          })
                        : n.toFixed(4);
                    return (
                      <div
                        key={trade.id}
                        style={{
                          background: "rgba(255,255,255,0.03)",
                          borderRadius: "12px",
                          border: `1px solid ${isWin ? "rgba(0,181,120,0.2)" : "rgba(255,82,82,0.2)"}`,
                          padding: "12px 14px",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            marginBottom: "8px",
                          }}
                        >
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "6px",
                            }}
                          >
                            <span
                              style={{
                                padding: "2px 8px",
                                borderRadius: "20px",
                                fontSize: "10px",
                                fontWeight: "bold",
                                color: "#fff",
                                background:
                                  trade.tradeType === "CALL"
                                    ? "#00b578"
                                    : "#ff5252",
                              }}
                            >
                              {trade.tradeType === "CALL" ? "↑ CALL" : "↓ PUT"}
                            </span>
                            <span
                              style={{
                                fontSize: "11px",
                                color: "#d1d5db",
                                fontWeight: "bold",
                              }}
                            >
                              {trade.pair.replace("USDT", "")} / USDT
                            </span>
                            <span
                              style={{
                                fontSize: "10px",
                                fontWeight: "bold",
                                padding: "1px 6px",
                                borderRadius: "4px",
                                color: isWin ? "#00b578" : "#ff5252",
                                background: isWin
                                  ? "rgba(0,181,120,0.12)"
                                  : "rgba(255,82,82,0.12)",
                                border: `1px solid ${isWin ? "rgba(0,181,120,0.3)" : "rgba(255,82,82,0.3)"}`,
                              }}
                            >
                              {isWin ? "WIN" : "LOSS"}
                            </span>
                          </div>
                          <span
                            style={{
                              fontSize: "14px",
                              fontWeight: "bold",
                              color: isWin ? "#00b578" : "#ff5252",
                            }}
                          >
                            {isWin ? "+" : ""}
                            {trade.profit.toFixed(4)} USDT
                          </span>
                        </div>
                        <div
                          style={{
                            display: "grid",
                            gridTemplateColumns: "repeat(4, 1fr)",
                            gap: "6px",
                            borderTop: "1px solid rgba(255,255,255,0.06)",
                            paddingTop: "8px",
                            marginBottom: "6px",
                          }}
                        >
                          {[
                            {
                              label: "Entry",
                              value: `$${fmtP(trade.entryPrice)}`,
                            },
                            {
                              label: "Exit",
                              value:
                                trade.exitPrice > 0
                                  ? `$${fmtP(trade.exitPrice)}`
                                  : "—",
                            },
                            { label: "Lot", value: `${trade.lotSize} USDT` },
                            { label: "Leverage", value: `${trade.leverage}x` },
                          ].map(({ label, value }) => (
                            <div key={label} style={{ textAlign: "center" }}>
                              <div
                                style={{
                                  fontSize: "9px",
                                  color: "#6b7280",
                                  textTransform: "uppercase",
                                  letterSpacing: "0.5px",
                                  marginBottom: "2px",
                                }}
                              >
                                {label}
                              </div>
                              <div
                                style={{
                                  fontSize: "10px",
                                  color: "#d1d5db",
                                  fontWeight: "500",
                                }}
                              >
                                {value}
                              </div>
                            </div>
                          ))}
                        </div>
                        {trade.entryTime && (
                          <div style={{ fontSize: "10px", color: "#4b5563" }}>
                            {trade.entryTime.toLocaleString()}
                          </div>
                        )}
                      </div>
                    );
                  })
                ) : (
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      padding: "20px 0",
                      color: "#9ca3af",
                    }}
                  >
                    <svg
                      width="36"
                      height="36"
                      fill="currentColor"
                      style={{ opacity: 0.3, marginBottom: "4px" }}
                      viewBox="0 0 24 24"
                    >
                      <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                    </svg>
                    <span style={{ fontSize: "11px" }}>No closed orders</span>
                  </div>
                )}
              </div>
            );
          })()}

        {(tab === 2 || tab === 3) && (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              padding: "20px 0",
              color: "#9ca3af",
            }}
          >
            <svg
              width="36"
              height="36"
              fill="currentColor"
              style={{ opacity: 0.3, marginBottom: "4px" }}
              viewBox="0 0 24 24"
            >
              <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
            </svg>
            <span style={{ fontSize: "11px" }}>No records</span>
          </div>
        )}
      </div>

      {/* ── Last-10-second countdown overlay ───────────────────────────── */}
      {activeTrade && candleCountdown <= 10 && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 600,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: "rgba(0,0,0,0.78)",
            backdropFilter: "blur(6px)",
          }}
        >
          <div style={{ textAlign: "center" }}>
            <div
              style={{
                fontSize: "11px",
                color: "#9ca3af",
                letterSpacing: "3px",
                marginBottom: "8px",
                textTransform: "uppercase",
              }}
            >
              Trade Closing In
            </div>

            <div
              style={{
                fontSize: "96px",
                fontWeight: "900",
                lineHeight: 1,
                color: activeTrade.type === "call" ? "#00b578" : "#ff5252",
                textShadow: `0 0 48px ${
                  activeTrade.type === "call" ? "#00b578" : "#ff5252"
                }`,
              }}
            >
              {candleCountdown}
            </div>

            <div
              style={{
                marginTop: "16px",
                display: "inline-flex",
                alignItems: "center",
                gap: "8px",
                padding: "6px 20px",
                borderRadius: "20px",
                background:
                  activeTrade.type === "call"
                    ? "rgba(0,181,120,0.15)"
                    : "rgba(255,82,82,0.15)",
                border: `1px solid ${
                  activeTrade.type === "call" ? "#00b578" : "#ff5252"
                }`,
                fontSize: "14px",
                fontWeight: "bold",
                color: activeTrade.type === "call" ? "#00b578" : "#ff5252",
              }}
            >
              {activeTrade.type === "call" ? "↑ CALL" : "↓ PUT"}
            </div>

            <div
              style={{
                marginTop: "16px",
                display: "flex",
                flexDirection: "column",
                gap: "4px",
                fontSize: "13px",
                color: "#9ca3af",
              }}
            >
              <span>
                Entry:{" "}
                <span style={{ color: "#f5a623", fontWeight: "bold" }}>
                  {activeTrade.entryPrice.toLocaleString("en-US", {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 5,
                  })}
                </span>
              </span>
              <span>
                Current:{" "}
                <span style={{ color: "#d1d5db", fontWeight: "bold" }}>
                  {coin?.lastPrice
                    ? Number(coin.lastPrice).toLocaleString("en-US", {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 5,
                      })
                    : "—"}
                </span>
              </span>
              <span>
                Position:{" "}
                <span style={{ color: "#d1d5db" }}>
                  {(activeTrade.amount * activeTrade.leverage).toFixed(2)} USDT
                  @ {activeTrade.leverage}x
                </span>
              </span>
            </div>
          </div>
        </div>
      )}

      {/* ── Candle closing overlay ──────────────────────────────────────── */}
      {candleCountdown <= 10 && !activeTrade && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 600,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: "rgba(0,0,0,0.75)",
            backdropFilter: "blur(6px)",
          }}
        >
          <div style={{ textAlign: "center" }}>
            <div
              style={{
                fontSize: "11px",
                color: "#9ca3af",
                letterSpacing: "3px",
                marginBottom: "8px",
                textTransform: "uppercase",
              }}
            >
              New Candle In
            </div>

            <div
              style={{
                fontSize: "96px",
                fontWeight: "900",
                lineHeight: 1,
                color: candleCountdown <= 5 ? "#ff5252" : "#f5a623",
                textShadow: `0 0 48px ${candleCountdown <= 5 ? "#ff5252" : "#f5a623"}`,
                fontVariantNumeric: "tabular-nums",
              }}
            >
              {candleCountdown}
            </div>

            <div
              style={{
                marginTop: "16px",
                fontSize: "13px",
                color: "#6b7280",
                letterSpacing: "1px",
              }}
            >
              Wait to place order on next candle
            </div>
          </div>
        </div>
      )}

      {/* ── Order Confirmation modal ────────────────────────────────────── */}
      {pendingOrder && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 400,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: "rgba(0,0,0,0.85)",
            backdropFilter: "blur(8px)",
          }}
        >
          <div
            style={{
              background: "#131920",
              borderRadius: "20px",
              padding: "28px 24px",
              width: "90%",
              maxWidth: "340px",
              border: `1px solid ${pendingOrder.type === "call" ? "rgba(0,181,120,0.4)" : "rgba(255,82,82,0.4)"}`,
              boxShadow: `0 0 60px ${pendingOrder.type === "call" ? "rgba(0,181,120,0.2)" : "rgba(255,82,82,0.2)"}`,
            }}
          >
            <div style={{ textAlign: "center", marginBottom: "20px" }}>
              <span
                style={{
                  display: "inline-block",
                  padding: "4px 18px",
                  borderRadius: "20px",
                  fontSize: "13px",
                  fontWeight: "bold",
                  color: "#fff",
                  background:
                    pendingOrder.type === "call" ? "#00b578" : "#ff5252",
                  letterSpacing: "1px",
                  marginBottom: "10px",
                }}
              >
                {pendingOrder.type === "call" ? "↑ CALL" : "↓ PUT"}
              </span>
              <div
                style={{
                  fontSize: "16px",
                  fontWeight: "bold",
                  color: "#ffffff",
                }}
              >
                Confirm Order
              </div>
            </div>

            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "10px",
                background: "rgba(255,255,255,0.03)",
                borderRadius: "12px",
                padding: "14px 16px",
                marginBottom: "20px",
                border: "1px solid rgba(255,255,255,0.06)",
              }}
            >
              {[
                { label: "Pair", value: `${base} / USDT` },
                {
                  label: "Direction",
                  value: pendingOrder.type.toUpperCase(),
                  color: pendingOrder.type === "call" ? "#00b578" : "#ff5252",
                },
                {
                  label: "Entry Price",
                  value: pendingOrder.entryPrice.toLocaleString("en-US", {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 5,
                  }),
                  color: "#f5a623",
                },
                {
                  label: "Lot Size",
                  value: `${pendingOrder.amount.toFixed(2)} USDT`,
                },
                { label: "Leverage", value: `${pendingOrder.leverage}x` },
                {
                  label: "Position",
                  value: `${(pendingOrder.amount * pendingOrder.leverage).toFixed(2)} USDT`,
                },
                { label: "Timeframe", value: `${pendingOrder.timeframe} min` },
              ].map(({ label, value, color }) => (
                <div
                  key={label}
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <span style={{ fontSize: "12px", color: "#6b7280" }}>
                    {label}
                  </span>
                  <span
                    style={{
                      fontSize: "13px",
                      fontWeight: "bold",
                      color: color || "#d1d5db",
                    }}
                  >
                    {value}
                  </span>
                </div>
              ))}
            </div>

            <div style={{ display: "flex", gap: "10px" }}>
              <button
                onClick={() => setPendingOrder(null)}
                style={{
                  flex: 1,
                  padding: "13px 0",
                  borderRadius: "12px",
                  fontSize: "14px",
                  fontWeight: "bold",
                  color: "#9ca3af",
                  background: "rgba(255,255,255,0.06)",
                  border: "1px solid rgba(255,255,255,0.1)",
                  cursor: "pointer",
                }}
              >
                Reject
              </button>
              <button
                onClick={confirmTrade}
                style={{
                  flex: 1,
                  padding: "13px 0",
                  borderRadius: "12px",
                  fontSize: "14px",
                  fontWeight: "bold",
                  color: "#ffffff",
                  background:
                    pendingOrder.type === "call"
                      ? "linear-gradient(135deg, #00b578, #089981)"
                      : "linear-gradient(135deg, #ff5252, #e03030)",
                  border: "none",
                  cursor: "pointer",
                }}
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Order placed success toast ───────────────────────────────────── */}
      {orderSuccess && (
        <div
          style={{
            position: "fixed",
            top: "44px",
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: 750,
            background: "#131920",
            border: "1px solid rgba(0,181,120,0.5)",
            borderRadius: "14px",
            padding: "14px 24px",
            display: "flex",
            alignItems: "center",
            gap: "10px",
            boxShadow: "0 4px 32px rgba(0,181,120,0.25)",
            whiteSpace: "nowrap",
          }}
        >
          <div
            style={{
              width: "28px",
              height: "28px",
              borderRadius: "50%",
              background: "rgba(0,181,120,0.15)",
              border: "1.5px solid #00b578",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <svg
              width="14"
              height="14"
              fill="none"
              stroke="#00b578"
              strokeWidth={2.5}
              viewBox="0 0 24 24"
            >
              <path
                d="M5 13l4 4L19 7"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
          <span
            style={{ fontSize: "14px", fontWeight: "bold", color: "#ffffff" }}
          >
            {orderSuccess}
          </span>
        </div>
      )}
    </div>
  );
}
